import { DeleteOutlined, EditOutlined, MinusCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Card, Col, Divider, Form, Input, InputNumber, Layout, message, Modal, Popconfirm, Row, Select, Space, Table, Typography, Upload } from 'antd';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/DataContext';
import { db, storage } from '../../firebase';
import apis from '../../apis';
const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

// Tiers based on SystemSettings AWARD_TIERS
const AREA_TYPES = ['Lento', 'Allegro', 'Presto'];

const generateId = () => Math.random().toString(36).substring(2, 10);

const VenueSettings = () => {
    const { currentEventId } = useAuth();
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingVenue, setEditingVenue] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadedImageUrl, setUploadedImageUrl] = useState('');
    const [form] = Form.useForm();

    useEffect(() => {
        if (currentEventId) {
            fetchEventData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentEventId]);

    const fetchEventData = async () => {
        setLoading(true);
        try {
            const docRef = doc(db, 'events', currentEventId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setVenues(data.venues || []);
            } else {
                message.error('Event not found');
            }
        } catch (error) {
            console.error('Error fetching event data:', error);
            message.error('Failed to fetch event data');
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (options) => {
        const { file, onSuccess, onError } = options;
        if (!file) return;

        setUploadingImage(true);
        try {
            const storageRef = ref(storage, `venues/${currentEventId}/${file.name}_${Date.now()}`);
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    // Progress can be monitored here if needed
                },
                (error) => {
                    console.error('Upload failed:', error);
                    message.error('Image upload failed');
                    onError(error);
                    setUploadingImage(false);
                },
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        setUploadedImageUrl(downloadURL);
                        form.setFieldsValue({ imageUrl: downloadURL });
                        message.success('Image uploaded successfully');
                        onSuccess(null, file);
                    } catch (error) {
                        console.error('Image URL lookup failed:', error);
                        message.error('Could not retrieve the uploaded image URL.');
                        onError(error);
                    } finally {
                        setUploadingImage(false);
                    }
                }
            );
        } catch (error) {
            console.error('Error in handleUpload:', error);
            onError(error);
            setUploadingImage(false);
        }
    };

    const handleSave = async (values) => {
        if (uploadingImage) {
            message.error('Wait for the image upload to finish before saving.');
            return;
        }
        try {
            setLoading(true);
            const docRef = doc(db, 'events', currentEventId);

            const newVenue = {
                id: editingVenue ? editingVenue.id : values.id,
                label: values.label,
                imageUrl: uploadedImageUrl || values.imageUrl,
                seatConfig: values.seatConfig || [],
                sessions: editingVenue ? (editingVenue.sessions || {}) : {},
            };

            // Image/name-only edits use the authenticated server path. The server
            // merges into the latest venue rather than replacing its schedule.
            if (editingVenue && JSON.stringify(newVenue.seatConfig) === JSON.stringify(editingVenue.seatConfig || [])) {
                const result = await apis.venueSettings.updateMetadata(currentEventId, editingVenue.id, {
                    label: newVenue.label,
                    imageUrl: newVenue.imageUrl,
                    expected: { label: editingVenue.label, imageUrl: editingVenue.imageUrl || '' },
                });
                setVenues(result.venues);
                message.success('Venue saved successfully');
                setIsModalVisible(false);
                return;
            }

            let updatedVenues;
            if (editingVenue) {
                updatedVenues = venues.map(v => v.id === editingVenue.id ? newVenue : v);
            } else {
                // Ensure ID is unique
                if (venues.find(v => v.id === newVenue.id)) {
                    message.error('Venue ID must be unique');
                    setLoading(false);
                    return;
                }
                updatedVenues = [...venues, newVenue];
            }

            await updateDoc(docRef, { venues: updatedVenues });
            message.success('Venue saved successfully');
            setVenues(updatedVenues);
            setIsModalVisible(false);
        } catch (error) {
            console.error('Error saving venue:', error);
            message.error(error.message || 'Failed to save venue');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            const docRef = doc(db, 'events', currentEventId);
            const updatedVenues = venues.filter(v => v.id !== id);
            await updateDoc(docRef, { venues: updatedVenues });
            message.success('Venue deleted successfully');
            setVenues(updatedVenues);
        } catch (error) {
            console.error('Error deleting venue:', error);
            message.error('Failed to delete venue');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (venue = null) => {
        setEditingVenue(venue);
        setUploadedImageUrl(venue ? venue.imageUrl : '');
        if (venue) {
            form.setFieldsValue({
                id: venue.id,
                label: venue.label,
                imageUrl: venue.imageUrl,
                seatConfig: venue.seatConfig || [],
            });
        } else {
            form.resetFields();
            // Default ID for convenience
            form.setFieldsValue({ id: `Venue_${generateId()}`, seatConfig: [] });
        }
        setIsModalVisible(true);
    };

    const columns = [
        {
            title: 'Venue ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Venue Name',
            dataIndex: 'label',
            key: 'label',
        },
        {
            title: 'Image',
            dataIndex: 'imageUrl',
            key: 'imageUrl',
            render: (url) => url ? <img src={url} alt="venue" style={{ height: '40px', borderRadius: '4px' }} /> : 'No Image',
        },
        {
            title: 'Configured Rows',
            key: 'rows',
            render: (_, record) => record.seatConfig?.length || 0,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => openModal(record)}>Edit</Button>
                    <Popconfirm title="Are you sure?" onConfirm={() => handleDelete(record.id)}>
                        <Button danger icon={<DeleteOutlined />}>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
            <Content style={{ padding: '24px' }}>
                <Card title={<Title level={3} style={{ margin: 0 }}>Venue Settings</Title>}
                    extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>Add Venue</Button>}>

                    <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                        Manage event venues, floor plans, and seat layouts. The seat configuration here is used to generate actual seats when a new Orchestra Session is created.
                    </Text>

                    <Table
                        columns={columns}
                        dataSource={venues}
                        rowKey="id"
                        loading={loading}
                        pagination={false}
                    />
                </Card>

                <Modal
                    title={editingVenue ? "Edit Venue" : "Add Venue"}
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    footer={null}
                    width={800}
                    destroyOnClose
                >
                    <Form form={form} layout="vertical" onFinish={handleSave}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="id"
                                    label="Venue ID (Internal)"
                                    rules={[{ required: true, message: 'ID is required' }]}
                                >
                                    <Input disabled={!!editingVenue} placeholder="e.g. Venue1" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="label"
                                    label="Venue Name (Display)"
                                    rules={[{ required: true, message: 'Name is required' }]}
                                >
                                    <Input placeholder="e.g. Jatayu Hall" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item label="Venue Image" name="imageUrl" rules={[{ required: true, message: 'Image is required' }]}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <Upload
                                    customRequest={handleUpload}
                                    showUploadList={false}
                                    accept="image/*"
                                >
                                    <Button icon={<UploadOutlined />} loading={uploadingImage}>
                                        Upload Image
                                    </Button>
                                </Upload>
                                {uploadedImageUrl && (
                                    <img src={uploadedImageUrl} alt="Preview" style={{ height: '60px', borderRadius: '4px', border: '1px solid #d9d9d9' }} />
                                )}
                            </div>
                        </Form.Item>

                        <Divider>Seat Configuration</Divider>
                        <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                            Define rows and capacities. When a session is created for this venue, individual seat tickets will be generated based on this configuration.
                        </Text>

                        <Form.List name="seatConfig">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'row']}
                                                rules={[{ required: true, message: 'Missing row letter' }]}
                                            >
                                                <Input placeholder="Row (e.g. A)" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'seatCount']}
                                                rules={[{ required: true, message: 'Missing capacity' }]}
                                            >
                                                <InputNumber placeholder="No. of Seats" min={1} max={100} />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'areaType']}
                                                rules={[{ required: true, message: 'Missing area tier' }]}
                                            >
                                                <Select placeholder="Select Tier" style={{ width: 120 }}>
                                                    {AREA_TYPES.map(tier => <Option key={tier} value={tier}>{tier}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            Add Row Configuration
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>

                        <Form.Item style={{ textAlign: 'right', marginTop: '24px' }}>
                            <Button onClick={() => setIsModalVisible(false)} style={{ marginRight: 8 }}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={loading} disabled={uploadingImage}>Save Venue</Button>
                        </Form.Item>
                    </Form>
                </Modal>
            </Content>
        </Layout>
    );
};

export default VenueSettings;
